import AntIcon from "@/components/icons/AntIcon";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { App as AntApp, ConfigProvider, GetProp, Steps, StepsProps, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { CompetencePageCheck } from "../../../../../app/contexts/CompetenceCheckContext";
import { useGetCalcDraft, useGetCalcRecordRequestResponse, useGetContract } from "../../../../../common/api/queries";
import { QueryKeys } from "../../../../../common/constants";
import { FieldConstraintViolation } from "../../../../../common/types";
import { FinancialSector } from "../../../../agent/enums";
import { ClientFormType } from "../../../../client/enums";
import { Client } from "../../../../client/types";
import { DASHBOARD_ROUTE_PATHS } from "../../../../dashboard/paths";
import { CALC_ROUTE_PATHS } from "../../../paths";
import { OperationType } from "../../../records/enums";
import { TravelCalcRecordRequestResponse } from "../../../records/types";
import { CalcLoadingModal } from "../../components/CalcLoadingModal";
import { OperationStage } from "../../realty/enums";
import { CalcResult } from "../../types";
import { createCalcGenDataFromContract, isApplicableResult, isTravelGenData } from "../../utils";
import { TravelCalcWrapper } from "../components/calc/TravelCalcWrapper";
import { TravelGenWrapper } from "../components/gen/TravelGenWrapper";
import { TravelCalcResultsView } from "../components/result/TravelCalcResultsView";
import { TravelCalcResultsProvider, useTravelCalcResults } from "../contexts";
import { TravelCalc, TravelCalcResultData, TravelFormClients, TravelGen, TravelGenForm } from "../types";
import {
  createTravelCalcObjectFromCalcData,
  createTravelCalcObjectFromGenData,
  createTravelFormClientsObject,
  createTravelGenFormDataObject,
} from "../utils";

type StepItem = {
  title: React.ReactNode;
  icon: React.ReactNode;
  content?: React.ReactNode;
};

enum TRAVEL_CALC_STEPS {
  FIRST_STEP_CALC = 0,
  SECOND_STEP_RESULTS = 1,
  THIRD_STEP_GENERATE_CONTRACT = 2,
}

export const TravelCalcContainer = () => (
  <TravelCalcResultsProvider>
    <TravelCalcContainerContent />
  </TravelCalcResultsProvider>
);

const TravelCalcContainerContent = () => {
  const { t } = useTranslation();
  const { modal, notification } = AntApp.useApp();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const recordId = searchParams.get(QueryKeys.RECORD_ID);
  const draftId = searchParams.get(QueryKeys.DRAFT_ID);
  const contractId = searchParams.get(QueryKeys.CONTRACT_ID);

  const [currentStepIndex, setCurrentStepIndex] = useState<TRAVEL_CALC_STEPS>(TRAVEL_CALC_STEPS.FIRST_STEP_CALC);
  const [lastAvailableStepIndex, setLastAvailableStepIndex] = useState<number>(TRAVEL_CALC_STEPS.FIRST_STEP_CALC);
  const [selectedResult, setSelectedResult] = useState<CalcResult<TravelCalcResultData>>();
  const [calcData, setCalcData] = useState<TravelCalc>();
  const [genData, setGenData] = useState<TravelGenForm>();
  const [clients, setClients] = useState<TravelFormClients>({
    policyHolder: undefined,
    representative: undefined,
  });

  const [calcDataFormErrors, setCalcDataFormErrors] = useState<FieldConstraintViolation[]>([]);
  const [isCalcFormChanged, setIsCalcFormChanged] = useState(false);
  const [calculationCount, setCalculationCount] = useState(0);

  const { data: calcRecord, isError: isErrorCalcRecord } =
    useGetCalcRecordRequestResponse<TravelCalcRecordRequestResponse>(recordId ?? "");
  const { data: calcDraft, isError: isErrorCalcDraft } = useGetCalcDraft(draftId ?? "");
  const { data: contract, isError: isErrorContract } = useGetContract(contractId ?? "");
  const { applicableResults, calculate, clearResults } = useTravelCalcResults();

  useEffect(() => {
    if (isErrorCalcRecord || isErrorCalcDraft || isErrorContract) {
      clearResults();
      navigate(CALC_ROUTE_PATHS.travel.to, { replace: true });
    }
  }, [isErrorCalcRecord, isErrorCalcDraft, isErrorContract]);

  const initFormDataFromGenData = (genData: TravelGen) => {
    const clients = createTravelFormClientsObject(genData);

    setCalcData(createTravelCalcObjectFromGenData(genData));
    setClients(clients);
    setGenData(createTravelGenFormDataObject(genData, clients));
  };

  useEffect(() => {
    if (calcData || !contract) {
      return;
    }

    const calcGenData = createCalcGenDataFromContract(contract);

    if (!calcGenData || !isTravelGenData(calcGenData)) {
      notification.error({
        title: t("common.error"),
        description: t("calc.helpers.loadDataError"),
        key: "loadCalcDataError",
      });

      clearResults();
      navigate(CALC_ROUTE_PATHS.travel.to, { replace: true });
      return;
    }

    notification.info({
      title: t("calc.helpers.calcDataInitTitle"),
      description: t("calc.helpers.calcDataInitFromContract"),
      duration: 10,
      key: "travelCalcDataInitFromContract",
    });

    initFormDataFromGenData(calcGenData);
  }, [contract]);

  useEffect(() => {
    if (calcData || !calcRecord) {
      return;
    }

    notification.info({
      title: t("calc.helpers.calcDataInitTitle"),
      description: t("calc.records.helpers.calcDataInit"),
      duration: 10,
      key: "travelCalcDataInit",
    });

    if (calcRecord.operationType === OperationType.CALCULATE) {
      setCalcData(createTravelCalcObjectFromCalcData(calcRecord.request));
    } else {
      initFormDataFromGenData(calcRecord.request);
    }
  }, [calcRecord]);

  useEffect(() => {
    if (calcData || !calcDraft?.draftData) {
      return;
    }

    notification.info({
      title: t("calc.draft.helpers.draftLoaded"),
      description: t("calc.draft.helpers.draftLoadedInfo"),
      duration: 10,
    });

    setCalcData(createTravelCalcObjectFromGenData(calcDraft.draftData));

    if (calcDraft.stage === OperationStage.GENERATE) {
      const clients = createTravelFormClientsObject(calcDraft.draftData);
      setClients(clients);
      setGenData(createTravelGenFormDataObject(calcDraft.draftData, clients));
    }
  }, [calcDraft?.draftData]);

  const handleCalculationFormSubmit = (calcData: TravelCalc) => {
    setCalcData(calcData);
    calculate(calcData, {
      onSuccess: (results) => {
        setCalculationCount((count) => count + 1);
        setIsCalcFormChanged(false);
        setCalcDataFormErrors([]);

        if (results.some(isApplicableResult)) {
          handleNextStep();
        } else if (results.length) {
          resetToCalcStep();
        }
      },
    });
  };

  const handleClientChange = (type: ClientFormType, client?: Partial<Client>) => {
    let updatedClients: TravelFormClients;

    switch (type) {
      case ClientFormType.POLICY_HOLDER:
        updatedClients = { ...clients, policyHolder: client };
        break;
      case ClientFormType.REPRESENTATIVE:
        updatedClients = { ...clients, representative: client };
        break;
      default:
        updatedClients = { ...clients };
        break;
    }
    setClients(updatedClients);
    return updatedClients;
  };

  const handleResetCalculatorClick = (onSuccess: () => void) => {
    modal.confirm({
      title: t("calc.helpers.resetCalculatorSubmit"),
      okText: t("common.yes"),
      cancelText: t("common.back"),
      onOk: () => {
        setClients({ policyHolder: undefined, representative: undefined });
        setCalcData(undefined);
        setGenData(undefined);
        setSelectedResult(undefined);

        clearResults();
        onSuccess();
        navigate(CALC_ROUTE_PATHS.travel.to, { replace: true });
      },
    });
  };

  const handleGenerateContractClick = (selectedResult: CalcResult<TravelCalcResultData>) => {
    setSelectedResult(selectedResult);
    handleNextStep();
  };

  const handleShowErrors = (errors: FieldConstraintViolation[]) => {
    setCalcDataFormErrors(errors);
    goToPreviousStep();
  };

  const handleNextStep = () => {
    setCurrentStepIndex(currentStepIndex + 1);

    if (lastAvailableStepIndex < currentStepIndex + 1) {
      setLastAvailableStepIndex(lastAvailableStepIndex + 1);
    }
  };

  const resetToCalcStep = () => {
    setCurrentStepIndex(TRAVEL_CALC_STEPS.FIRST_STEP_CALC);
    setLastAvailableStepIndex(TRAVEL_CALC_STEPS.FIRST_STEP_CALC);
  };

  const handleStepChange = (stepIndex: number) => {
    if (stepIndex === TRAVEL_CALC_STEPS.SECOND_STEP_RESULTS && applicableResults.length === 0) {
      resetToCalcStep();
    } else {
      setCurrentStepIndex(stepIndex);
    }
  };

  const goToPreviousStep = () => {
    setCurrentStepIndex(currentStepIndex - 1);
  };

  const goToNextStep = () => {
    setCurrentStepIndex(currentStepIndex + 1);
  };

  const stepItems: StepItem[] = [
    {
      title: <div style={{ fontSize: 18 }}>{t("calc.travel.sections.calculation")}</div>,
      icon: <AntIcon type="laptop" />,
      content: (
        <TravelCalcWrapper
          calcData={calcData}
          setIsFormChanged={() => setIsCalcFormChanged(true)}
          formErrors={calcDataFormErrors}
          onCalculationFormSubmit={handleCalculationFormSubmit}
          onResetCalculatorClick={handleResetCalculatorClick}
          goToCalcResults={isCalcFormChanged || applicableResults.length === 0 ? undefined : goToNextStep}
        />
      ),
    },
    {
      title: <div style={{ fontSize: 18 }}>{t("calc.travel.sections.insuranceSelection")}</div>,
      icon: <AntIcon type="file-search" />,
      content: calcData ? (
        <TravelCalcResultsView
          key={calculationCount}
          calcData={calcData}
          handleGenerateContract={handleGenerateContractClick}
          handleShowErrors={handleShowErrors}
          handleGoToPreviousStep={goToPreviousStep}
          draft={calcDraft}
        />
      ) : undefined,
    },
    {
      title: <div style={{ fontSize: 18 }}>{t("calc.travel.sections.contractGeneration")}</div>,
      icon: <AntIcon type="file-done" />,
      content:
        calcData && selectedResult ? (
          <TravelGenWrapper
            initialData={genData}
            calcData={calcData}
            clients={clients}
            selectedResult={selectedResult}
            draftId={draftId ?? undefined}
            onClientChange={handleClientChange}
            handleGoToPreviousStep={goToPreviousStep}
          />
        ) : undefined,
    },
  ];

  const steps: GetProp<StepsProps, "items"> = stepItems.map((item, index) => ({
    key: index,
    title: item.title,
    disabled: index > lastAvailableStepIndex || isCalcFormChanged,
    status: currentStepIndex >= index ? "finish" : "wait",
    icon: item.icon,
  }));

  return (
    <DisplayWrapper itemLoaded={recordId || draftId || contractId ? !!calcData : true}>
      <CompetencePageCheck
        sector={FinancialSector.INSURANCES_OR_REINSURANCES}
        redirectTo={DASHBOARD_ROUTE_PATHS.homepage.to}
      >
        <ContentWrapper>
          <div style={{ maxWidth: 800, margin: "10px auto 20px" }}>
            <ConfigProvider theme={{ components: { Steps: { customIconFontSize: 28 } } }}>
              <Steps onChange={handleStepChange} current={currentStepIndex} items={steps} />
            </ConfigProvider>
          </div>

          <Tabs
            activeKey={currentStepIndex.toString()}
            renderTabBar={() => <></>}
            animated
            items={[
              {
                key: TRAVEL_CALC_STEPS.FIRST_STEP_CALC.toString(),
                label: undefined,
                children: stepItems[TRAVEL_CALC_STEPS.FIRST_STEP_CALC]?.content,
              },
              {
                key: TRAVEL_CALC_STEPS.SECOND_STEP_RESULTS.toString(),
                label: undefined,
                children: stepItems[TRAVEL_CALC_STEPS.SECOND_STEP_RESULTS]?.content,
              },
              {
                key: TRAVEL_CALC_STEPS.THIRD_STEP_GENERATE_CONTRACT.toString(),
                label: undefined,
                children: stepItems[TRAVEL_CALC_STEPS.THIRD_STEP_GENERATE_CONTRACT]?.content,
              },
            ]}
          />

          <CalcLoadingModal />
        </ContentWrapper>
      </CompetencePageCheck>
    </DisplayWrapper>
  );
};
